import { ChangeDetectionStrategy, Component, inject, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { ConfigConstants, ConfigService, isDefined } from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, of, Subject } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    mergeMap,
    pairwise,
    startWith,
    switchMap,
    take,
    takeUntil,
    withLatestFrom,
} from 'rxjs/operators';
import { updateFunctionalElementRemarks, updateValue } from '../../store/actions/functional-elements.actions';
import {
    getFormCheckpointByXmlIdFactory,
    getFormFilters,
    getFormFunctionalElementByIdFactory,
    getFormParticipantCopyReadonly,
    getFormRadioOptionsByXmlIdFactory,
    getFormSelectedParticipantCopyId,
    getFormTypeFilters,
    isCosimaFunctionalElementFromTrainingChecklistReadOnly,
} from '../../store/selectors/form.selector';
import { FormsState } from '../../store/state/pace-forms.state';
import { Checkpoint } from '../../types/checkpoint';
import { CHOICE_TYPE } from '../../types/choice';
import { ContentId } from '../../types/content-block';
import { FormFilters } from '../../types/form-filter';
import { FormTypeFilters } from '../../types/form-type-filters';
import { FormsConstants } from '../../types/forms-constants';
import { FUNCTIONAL_ELEMENT_TYPE } from '../../types/functional-element';
import { ParticipantCopyId } from '../../types/participant-copy';
import { RADIO_OPTION_TYPE, RadioOption } from '../../types/radio-option';
import { initialValueState, ValueFunctionalElement } from '../../types/value-functional-element';
import { isAstoriaFunctionalElementVisible, isCosimaFunctionalElementVisible } from '../../utilities/answer-utilities';
import { FormUtilities } from '../../utilities/form-utilities';
import { ValueUtilities } from '../../utilities/value-utilities';

@Component({
    selector: 'pace-forms-value',
    templateUrl: './value.component.html',
    styleUrls: ['./value.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ValueComponent implements OnInit, OnDestroy {
    private readonly configService = inject(ConfigService);
    @Input()
    public id: ContentId;

    @Input()
    public parentForm: FormArray | undefined;

    private readOnly$: Observable<boolean>;
    public valueFunctionalElement$: Observable<ValueFunctionalElement>;
    private placeholder: BehaviorSubject<string>;
    public placeholder$: Observable<string>;
    public inputMode$: Observable<string>;
    public controllingCheckpoint$: Observable<Checkpoint | undefined>;
    public options$: Observable<RadioOption[] | undefined>;
    public selectedParticipant$: Observable<ParticipantCopyId>;

    public typeFilters$: Observable<FormTypeFilters>;
    public formFilters$: Observable<FormFilters>;
    public elementTypeLabel$: Observable<string>;
    public visible$: Observable<boolean>;
    public showRemarksField$: Observable<boolean>;
    public displayNACheckbox$: Observable<boolean>;
    public displayHistoryButton$: Observable<boolean>;

    public valueGroup: FormGroup;
    public inputFieldControl: FormControl;
    public checkboxControl: FormControl;
    public remarksControl: FormControl;

    public faCheck = faCheck;

    private destroy$ = new Subject<void>();
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly formBuilder: FormBuilder,
        private readonly store: Store<FormsState>,
    ) {
        this.typeFilters$ = store.select(getFormTypeFilters);
        this.formFilters$ = store.select(getFormFilters);
        this.selectedParticipant$ = store.select(getFormSelectedParticipantCopyId);
        this.placeholder = new BehaviorSubject<string>('');
        this.placeholder$ = this.placeholder.asObservable();
    }

    public ngOnInit(): void {
        this.inputFieldControl = this.formBuilder.control('');
        this.checkboxControl = this.formBuilder.control(false);
        this.remarksControl = this.formBuilder.control('');
        this.valueGroup = this.formBuilder.group({
            inputField: this.inputFieldControl,
            checkbox: this.checkboxControl,
            remarks: this.remarksControl,
        });
        if (this.parentForm) {
            const parent = this.parentForm;
            Promise.resolve(undefined)
                .then(() => {
                    parent.push(this.valueGroup);
                })
                .catch((error: Error) =>
                    this.log.error(
                        'ValueComponent: ngOnInit: Error in parentForm.push: ',
                        JSON.stringify(error, Object.getOwnPropertyNames(error)),
                    ),
                );
        }

        this.valueFunctionalElement$ = this.store
            .select(getFormFunctionalElementByIdFactory<ValueFunctionalElement>({ id: this.id }))
            .pipe(filter(isDefined));

        this.displayHistoryButton$ = this.valueFunctionalElement$.pipe(
            map(
                (valueFunctionalElement) =>
                    isDefined(valueFunctionalElement.uniqueId) &&
                    valueFunctionalElement.uniqueId.length > 0 &&
                    this.configService.getEnableFormsHistoricalValues(),
            ),
        );

        this.readOnly$ = combineLatest([
            this.store.select(getFormParticipantCopyReadonly),
            this.store.select(isCosimaFunctionalElementFromTrainingChecklistReadOnly({ id: this.id })),
        ]).pipe(map(([readOnly, isCosimaReadOnly]) => readOnly || isCosimaReadOnly));

        this.elementTypeLabel$ = combineLatest([
            this.valueFunctionalElement$.pipe(
                map((v) => v.elementType),
                filter(isDefined),
            ),
            this.store.select(getFormTypeFilters),
        ]).pipe(map(([elementType, filters]) => FormUtilities.getFilterLabel(elementType, filters)));

        this.inputMode$ = this.valueFunctionalElement$.pipe(
            map((valueFunctionalElement) =>
                // VALUE allows only numbers, whereas VALUE_UNIT allows any input
                valueFunctionalElement.type === FUNCTIONAL_ELEMENT_TYPE.VALUE ? 'decimal' : 'text',
            ),
        );

        this.controllingCheckpoint$ = this.valueFunctionalElement$.pipe(
            mergeMap((valueFunctionalElement) =>
                valueFunctionalElement.controllingCheckpoint.length > 0
                    ? this.store
                          .select(
                              getFormCheckpointByXmlIdFactory({ xmlId: valueFunctionalElement.controllingCheckpoint }),
                          )
                          .pipe(take(1))
                    : of(undefined),
            ),
        );

        this.options$ = this.controllingCheckpoint$.pipe(
            switchMap((checkpoint) =>
                typeof checkpoint !== 'undefined'
                    ? this.store.select(getFormRadioOptionsByXmlIdFactory({ xmlId: checkpoint.xmlId }))
                    : of(undefined),
            ),
        );

        this.visible$ = combineLatest([
            this.valueFunctionalElement$,
            this.controllingCheckpoint$,
            this.options$,
            this.typeFilters$,
            this.formFilters$,
            this.selectedParticipant$,
        ]).pipe(
            map(([valueFunctionalElement, checkpoint, options, typeFilters, formFilters, selectedParticipant]) => {
                const isMasterCopy = selectedParticipant === FormsConstants.MASTER_PARTICIPANT_ID;

                // The master displays everything. In a participant copy, we display only content that was filled out in
                // the current session and not loaded from XML.
                let visible = isMasterCopy || !valueFunctionalElement.contentLoadedFromXml;

                if (isDefined(valueFunctionalElement.choices)) {
                    // in participant copies, only content that was answered with "Yes" in this session is displayed
                    const selectedInMaster = Object.values(valueFunctionalElement.choices).find(
                        (c) => c?.selectedInMaster,
                    );
                    const yesInMaster =
                        typeof selectedInMaster !== 'undefined' &&
                        selectedInMaster.type === CHOICE_TYPE.CHOICE_POSITIVE;
                    const visibleInParticipantCopy = !valueFunctionalElement.contentLoadedFromXml && yesInMaster;

                    // filters apply both to master and participant copies
                    const visibleByFilter = isCosimaFunctionalElementVisible(
                        valueFunctionalElement,
                        typeFilters,
                        formFilters,
                        valueFunctionalElement.elementType,
                    );

                    visible = (isMasterCopy || visibleInParticipantCopy) && visibleByFilter;
                } else {
                    // In addition to the rule above, a controlling checkpoint can control the visibility by either the type
                    // of the checkpoint and a selected matching filter, or by the chosen answer: In a participant copy, we
                    // display only values answered with "yes".
                    const visibleByFilter = isAstoriaFunctionalElementVisible(
                        valueFunctionalElement,
                        checkpoint?.elementType,
                        typeFilters,
                        formFilters,
                        checkpoint?.xmlId ? { [checkpoint.xmlId]: options } : {},
                    );
                    visible = visibleByFilter && visible;
                    if (typeof checkpoint !== 'undefined' && typeof options !== 'undefined') {
                        const selectedInMaster = options.find((o) => o.selectedInMaster);
                        const yesInMaster =
                            typeof selectedInMaster !== 'undefined' &&
                            selectedInMaster.optionType === RADIO_OPTION_TYPE.YES &&
                            !selectedInMaster.contentLoadedFromXml;
                        visible = visible && (isMasterCopy || yesInMaster);
                    }
                }

                return visible;
            }),
        );

        this.showRemarksField$ = this.valueFunctionalElement$.pipe(
            map(
                (valueFunctionalElement) =>
                    // the remarks field should be displayed only for cosima. Choices are available only in cosima
                    isDefined(valueFunctionalElement.choices) &&
                    !!(
                        valueFunctionalElement.remarks?.length ||
                        valueFunctionalElement.choices?.choiceNegative?.selected
                    ),
            ),
        );

        this.displayNACheckbox$ = this.valueFunctionalElement$.pipe(
            map(
                (valueFunctionalElement) =>
                    (!isDefined(valueFunctionalElement.choices) &&
                        valueFunctionalElement.controllingCheckpoint.length === 0) ||
                    isDefined(valueFunctionalElement.choices?.choiceNa),
            ),
        );

        this.remarksControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                withLatestFrom(this.valueFunctionalElement$),
                takeUntil(this.destroy$),
            )
            .subscribe(([newValue, valueFunctionalElement]: [string, ValueFunctionalElement]) => {
                if (!isDefined(valueFunctionalElement.xmlId)) {
                    throw new Error(`ValueComponent: remarksControl valueChanges: Functional element has no xml Id!`);
                }

                if (valueFunctionalElement.remarksXmlId) {
                    this.store.dispatch(
                        updateFunctionalElementRemarks({
                            id: valueFunctionalElement.id,
                            // using ! because remarksXmlId is guaranteed to be there in Cosima. It is only undefined in Astoria.
                            xmlId: valueFunctionalElement.remarksXmlId,
                            newValue,
                        }),
                    );
                }
            });

        combineLatest([this.valueFunctionalElement$.pipe(startWith(initialValueState), pairwise()), this.readOnly$])
            .pipe(takeUntil(this.destroy$))
            .subscribe(([[oldValueFunctionalElement, valueFunctionalElement], readOnly]) => {
                const validators = [];
                if (isDefined(valueFunctionalElement.lowerLimit)) {
                    validators.push(Validators.min(valueFunctionalElement.lowerLimit));
                }
                if (isDefined(valueFunctionalElement.upperLimit)) {
                    validators.push(Validators.max(valueFunctionalElement.upperLimit));
                }
                if (valueFunctionalElement.type === FUNCTIONAL_ELEMENT_TYPE.VALUE) {
                    validators.push(Validators.pattern('-?[0-9]+([,.][0-9]*)?'));
                }
                this.inputFieldControl.setValidators(validators);
                this.checkboxControl.patchValue(this.isNASelected(valueFunctionalElement), {
                    emitEvent: false,
                });
                if (valueFunctionalElement.controllingCheckpoint.length === 0) {
                    this.checkboxControl[valueFunctionalElement.disabledNACheckbox || readOnly ? 'disable' : 'enable']({
                        emitEvent: false,
                    });
                }
                this.inputFieldControl[
                    valueFunctionalElement.disabled || readOnly || this.checkboxControl.value ? 'disable' : 'enable'
                ]({
                    emitEvent: false,
                });
                this.remarksControl.patchValue(valueFunctionalElement.remarks, { emitEvent: false });
                this.remarksControl[valueFunctionalElement.disabled || readOnly ? 'disable' : 'enable']({
                    emitEvent: false,
                });
                const placeholderText = ValueUtilities.getPlaceholderText(valueFunctionalElement.function);
                this.placeholder.next(placeholderText);

                if (oldValueFunctionalElement.value !== valueFunctionalElement.value) {
                    if (valueFunctionalElement.value === FormsConstants.NA) {
                        this.inputFieldControl.patchValue(FormsConstants.NA);
                        this.placeholder.next(FormsConstants.NA);
                    } else {
                        const functionalElementValue = isDefined(valueFunctionalElement.function)
                            ? ValueUtilities.limitNumbers(valueFunctionalElement.value, valueFunctionalElement.function)
                            : valueFunctionalElement.value;
                        this.inputFieldControl.patchValue(functionalElementValue);
                        this.placeholder.next(placeholderText);
                    }
                }
            });

        this.inputFieldControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                startWith(initialValueState.value),
                pairwise(),
                filter(([oldValue, newValue]) => newValue === '' || oldValue !== newValue),
                withLatestFrom(this.valueFunctionalElement$, this.readOnly$),
                takeUntil(this.destroy$),
            )
            .subscribe(
                ([[, newValue], valueFunctionalElement, readOnly]: [
                    [string, string],
                    ValueFunctionalElement,
                    boolean,
                ]) => {
                    // if the control is disabled, don't do anything. It can still happen that the value is changed when
                    // a different participant copy is loaded.
                    if (readOnly) {
                        return;
                    }

                    if (
                        valueFunctionalElement.type === FUNCTIONAL_ELEMENT_TYPE.VALUE &&
                        isNaN(Number(newValue)) &&
                        newValue !== '-' &&
                        newValue !== FormsConstants.NA
                    ) {
                        const transformedValue = newValue.replace(',', '.');
                        const valueToPatch = !isNaN(Number(transformedValue))
                            ? transformedValue
                            : valueFunctionalElement.value;

                        this.inputFieldControl.patchValue(valueToPatch);
                        return;
                    }

                    this.store.dispatch(
                        updateValue({
                            xmlId: valueFunctionalElement.xmlId,
                            id: valueFunctionalElement.id,
                            newValue,
                            valueXmlId: valueFunctionalElement.valueXmlId,
                            valid: this.inputFieldControl.valid,
                        }),
                    );
                },
            );

        this.checkboxControl.valueChanges
            .pipe(withLatestFrom(this.valueFunctionalElement$, this.readOnly$), takeUntil(this.destroy$))
            .subscribe(([checkboxValue, valueFunctionalElement, readOnly]) => {
                // if the control is disabled, don't do anything. It can still happen that the value is changed when
                // a different participant copy is loaded.
                if (readOnly) {
                    return;
                }

                // if the value was not set to N.A. and the checkbox is now checked, we patch the control value
                // to N.A., which will trigger the valueChanges observable and dispatch the value to the state
                if (valueFunctionalElement.value !== FormsConstants.NA && checkboxValue) {
                    this.inputFieldControl.patchValue(FormsConstants.NA, { emitEvent: false, onlySelf: true });
                    // Since for VALUE we set the input field type to 'number', the patched value of 'N.A.' will not be
                    // accepted by the browser and not be displayed to the user. To give the feedback that the XML will
                    // be updated anyhow, we set the placeholder to 'N.A.' instead while the checkbox is ticked.
                    this.placeholder.next(FormsConstants.NA);
                } else if (valueFunctionalElement.value === FormsConstants.NA && !checkboxValue) {
                    // if the value was set to N.A. and the checkbox is now unchecked, we patch the control value
                    // to '', which will trigger the valueChanges observable and dispatch the value to the state
                    this.inputFieldControl.patchValue('', { emitEvent: false, onlySelf: true });
                    this.placeholder.next('');
                }
                this.inputFieldControl[checkboxValue ? 'disable' : 'enable']({ emitEvent: false });

                this.store.dispatch(
                    updateValue({
                        xmlId: valueFunctionalElement.xmlId,
                        id: valueFunctionalElement.id,
                        newValue: checkboxValue ? FormsConstants.NA : '',
                        valueXmlId: valueFunctionalElement.valueXmlId,
                        valid: this.inputFieldControl.valid,
                    }),
                );
            });
    }

    // This input method validates that the input contains only numeric values before triggering valueChanges.
    // Using <input type="text"> instead of <input type="number"> to allow entries like 'N.A.' when checkbox is selected.
    public validateNumberValue(event: Event): void {
        const input = event.target as HTMLInputElement;
        const transformedValue = input.value.replace(',', '.');
        const match = transformedValue.match(/^[-]?\d*\.?\d*/);
        const patchValue = match ? match[0] : '';
        this.inputFieldControl.patchValue(patchValue);
    }

    public ngOnDestroy(): void {
        // remove form controls from parent
        if (this.parentForm) {
            const index = this.parentForm.controls.findIndex((control) => control === this.valueGroup);
            this.parentForm.removeAt(index);
        }

        this.destroy$.next();
    }

    private isNASelected(valueFunctionalElement: ValueFunctionalElement): boolean {
        return (
            valueFunctionalElement.controllingCheckpoint.length === 0 &&
            valueFunctionalElement.value === FormsConstants.NA
        );
    }
}
